import { useQuery, useInfiniteQuery, useMutation, useTransport, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { keepPreviousData, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { useMemo } from 'react'
import type { Transport } from '@connectrpc/connect'

import { toast } from 'sonner'

import { listEvents, getEvent, getEventDetail, createEvent, updateEvent, archiveEvent, unarchiveEvent, getEventDriveTime, resolveLocationTimezone, getDashboardSummary } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEventsRequestSchema, GetEventRequestSchema, GetEventResponseSchema, GetEventDetailRequestSchema, GetEventDriveTimeRequestSchema, ResolveLocationTimezoneRequestSchema, ResolveLocationTimezoneStatus } from '@/gen/cortado/v1/cortado_pb'
import type { ListEventsResponse } from '@/gen/cortado/v1/cortado_pb'
import { ArchiveEventRequestSchema, UnarchiveEventRequestSchema, EventLiteSchema } from '@/gen/cortado/v1/inquiries_pb'
import { organizationStore } from './organization.store'
import { authStore } from './auth.store'
import { dateToTimestamp } from '@/lib/date-utils'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { connectTransport } from '@/lib/connect-transport'
import { EventListBucketEnum } from '@/gen/cortado/v1/settings_pb'

export interface ListEventsQueryOptions {
  organizationId: string
  bookingId?: string
  contactId?: string
  eventDateStart?: Date | Timestamp
  eventDateEnd?: Date | Timestamp
  contactNameFilter?: string
  listBucket?: EventListBucketEnum
  listBucketBoundary?: Date | Timestamp
  limit?: number
  offset?: number
  /** When true, archived events are returned alongside active ones. */
  includeArchived?: boolean
}

export function createListEventsRequest(options: ListEventsQueryOptions) {
  return create(ListEventsRequestSchema, {
    organizationId: options.organizationId,
    bookingId: options.bookingId,
    contactId: options.contactId,
    eventDateStart: options.eventDateStart ? (options.eventDateStart instanceof Date ? dateToTimestamp(options.eventDateStart) : options.eventDateStart) : undefined,
    eventDateEnd: options.eventDateEnd ? (options.eventDateEnd instanceof Date ? dateToTimestamp(options.eventDateEnd) : options.eventDateEnd) : undefined,
    contactNameFilter: options.contactNameFilter,
    listBucket: options.listBucket,
    listBucketBoundary: options.listBucketBoundary ? (options.listBucketBoundary instanceof Date ? dateToTimestamp(options.listBucketBoundary) : options.listBucketBoundary) : undefined,
    limit: options.limit,
    offset: options.offset,
    includeArchived: options.includeArchived,
  })
}

export function listEventsQueryOptions(
  options: ListEventsQueryOptions,
  transport: Transport = connectTransport,
) {
  return createQueryOptions(listEvents, createListEventsRequest(options), { transport })
}

export function getEventQueryOptions(eventId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getEvent, create(GetEventRequestSchema, { id: eventId }), { transport })
}

/**
 * Query options for the GetEventDetail aggregate RPC, which returns the event
 * plus its parent booking and (when present) the originating inquiry in a single
 * round-trip. Used by the event detail route loader to warm the per-entity
 * caches and collapse the event -> booking -> inquiry request waterfall.
 */
export function getEventDetailQueryOptions(eventId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getEventDetail, create(GetEventDetailRequestSchema, { id: eventId }), { transport })
}

export function useEventsList(options?: {
  bookingId?: string
  contactId?: string
  eventDateStart?: Date | Timestamp
  eventDateEnd?: Date | Timestamp
  contactNameFilter?: string
  listBucket?: EventListBucketEnum
  listBucketBoundary?: Date | Timestamp
  limit?: number
  offset?: number
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return createListEventsRequest({
      organizationId: selectedOrganizationId || '',
      bookingId: options?.bookingId,
      contactId: options?.contactId,
      eventDateStart: options?.eventDateStart,
      eventDateEnd: options?.eventDateEnd,
      contactNameFilter: options?.contactNameFilter,
      listBucket: options?.listBucket,
      listBucketBoundary: options?.listBucketBoundary,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.bookingId, options?.contactId, options?.eventDateStart, options?.eventDateEnd, options?.contactNameFilter, options?.listBucket, options?.listBucketBoundary, options?.limit, options?.offset])

  const query = useQuery(
    listEvents,
    request,
    {
      enabled: !!selectedOrganizationId && (options?.enabled ?? true),
      placeholderData: keepPreviousData,
    }
  )

  return {
    events: query.data?.events || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Page size for the paginated events list. */
const EVENTS_PAGE_SIZE = 100

/**
 * Paginated variant of {@link useEventsList} for the Events page.
 *
 * Fetches events in offset-based pages of {@link EVENTS_PAGE_SIZE} and exposes
 * `fetchNextPage`/`hasMore` for a "Load more" affordance. Previously the page
 * sent no limit and the backend silently capped the response at 50, hiding
 * older events and skewing the tab counts. Other consumers that only need a
 * bounded/flat list (e.g. the calendar) should keep using `useEventsList`.
 */
export function useInfiniteEventsList(options?: {
  bookingId?: string
  contactId?: string
  eventDateStart?: Date | Timestamp
  eventDateEnd?: Date | Timestamp
  contactNameFilter?: string
  listBucket?: EventListBucketEnum
  listBucketBoundary?: Date | Timestamp
  /**
   * When true, archived events are returned alongside active ones. Left
   * `undefined` (rather than `false`) when off so the query key is unchanged
   * for callers that never ask for archived rows.
   */
  includeArchived?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  // Init shape (not a created message) so `offset` is a definite number, which
  // connect-query's useInfiniteQuery requires for the page-param field.
  const input = useMemo(() => ({
    organizationId: selectedOrganizationId || '',
    bookingId: options?.bookingId,
    contactId: options?.contactId,
    eventDateStart: options?.eventDateStart
      ? (options.eventDateStart instanceof Date ? dateToTimestamp(options.eventDateStart) : options.eventDateStart)
      : undefined,
    eventDateEnd: options?.eventDateEnd
      ? (options.eventDateEnd instanceof Date ? dateToTimestamp(options.eventDateEnd) : options.eventDateEnd)
      : undefined,
    contactNameFilter: options?.contactNameFilter,
    listBucket: options?.listBucket,
    listBucketBoundary: options?.listBucketBoundary
      ? (options.listBucketBoundary instanceof Date ? dateToTimestamp(options.listBucketBoundary) : options.listBucketBoundary)
      : undefined,
    includeArchived: options?.includeArchived,
    limit: EVENTS_PAGE_SIZE,
    offset: 0,
  }), [selectedOrganizationId, options?.bookingId, options?.contactId, options?.eventDateStart, options?.eventDateEnd, options?.contactNameFilter, options?.listBucket, options?.listBucketBoundary, options?.includeArchived])

  const query = useInfiniteQuery(listEvents, input, {
    enabled: !!selectedOrganizationId,
    pageParamKey: 'offset',
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.events.length, 0)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    placeholderData: keepPreviousData,
  })

  const events = useMemo(
    () => query.data?.pages.flatMap((page) => page.events) ?? [],
    [query.data],
  )
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return {
    events,
    totalCount,
    hasMore: events.length < totalCount,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to get a single event with full enrichment
 *
 * This hook:
 * - Fetches the complete event data including:
 *   - Basic event fields (date, length, guests, timezone, stage)
 *   - Contact information (full contact object with embedded data)
 *   - Created/updated by users (full user objects)
 *   - Booking ID (reference to parent booking)
 *   - User scheduling IDs (array of IDs to fetch user schedulings separately)
 *   - Note IDs (array of IDs to fetch notes separately)
 *   - Task IDs (array of IDs to fetch tasks separately)
 *   - Scheduled users count
 * - Returns the full enriched event following the "stand on their own in full" pattern
 *
 * @param eventId - The ID of the event to fetch
 * @returns Query result with full event data
 */
export function useEvent(eventId: string) {
  const request = useMemo(() => {
    return create(GetEventRequestSchema, {
      id: eventId,
    })
  }, [eventId])

  const query = useQuery(
    getEvent,
    request,
    {
      enabled: !!eventId,
      select: (data) => data?.event,
    }
  )

  return {
    event: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to prefetch an event for smooth navigation
 * Used for hover interactions to load data before navigation
 */
export function usePrefetchEvent() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const prefetchEvent = async (eventId: string) => {
    const queryKey = createConnectQueryKey({
      schema: getEvent,
      transport,
      input: { id: eventId },
      cardinality: 'finite',
    })

    // Skip prefetch if data is already in cache
    const cachedData = queryClient.getQueryData(queryKey)
    if (cachedData) {
      return
    }

    await queryClient.prefetchQuery(
      getEventQueryOptions(eventId, transport)
    )
  }

  return { prefetchEvent }
}

/**
 * Hook to create a new event within a booking
 *
 * This hook:
 * - Creates a new event associated with a booking
 * - Automatically invalidates list queries to refresh booking events
 * - Returns the created event with full enrichment
 *
 * @returns Mutation object for creating events
 */
export function useCreateEvent() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createEvent, {
    onSuccess: () => {
      // Invalidate list queries to refresh the booking events table
      // refetchType: 'all' ensures inactive queries (e.g. calendar) are also refetched
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEvents,
          transport,
          cardinality: undefined,
        }),
        refetchType: 'all',
      })

      // Invalidate the dashboard aggregate so its event cards refresh too
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
        refetchType: 'all',
      })
    },
  })

  return { mutation }
}

/**
 * Hook to update an existing event
 *
 * This hook:
 * - Updates event fields (date, length, guests, timezone, stage, calendar)
 * - Updates individual event cache with the new data
 * - Invalidates list queries to refresh booking events
 *
 * @returns Mutation object for updating events
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listEventsKey = createConnectQueryKey({
    schema: listEvents,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(updateEvent, {
    onMutate: async () => {
      // Cancel in-flight list queries so they don't overwrite updates
      await queryClient.cancelQueries({ queryKey: listEventsKey })

      // Snapshot all list caches for rollback
      const previousLists = queryClient.getQueriesData<ListEventsResponse>({
        queryKey: listEventsKey,
      })

      return { previousLists }
    },

    onError: (_error, _variables, context) => {
      // Rollback all list caches to their pre-mutation state
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSuccess: (response) => {
      const updatedEvent = response.event
      if (!updatedEvent) return

      // Update individual event cache
      const queryKey = createConnectQueryKey({
        schema: getEvent,
        transport,
        input: { id: updatedEvent.id },
        cardinality: 'finite',
      })
      queryClient.setQueryData(
        queryKey,
        create(GetEventResponseSchema, { event: updatedEvent })
      )

      // Update all list caches with the response data
      const existingLists = queryClient.getQueriesData<ListEventsResponse>({
        queryKey: listEventsKey,
      })
      for (const [key, data] of existingLists) {
        if (!data?.events) continue
        queryClient.setQueryData(key, {
          ...data,
          events: data.events.map((event) => {
            if (event.id !== updatedEvent.id) return event
            return create(EventLiteSchema, {
              ...event,
              stage: updatedEvent.stage,
              eventDate: updatedEvent.eventDate,
              eventLength: updatedEvent.eventLength,
              eventGuests: updatedEvent.eventGuests,
              eventTimezone: updatedEvent.eventTimezone,
				eventTypeKey: updatedEvent.eventTypeKey,
              eventLocation: updatedEvent.eventLocation,
              baristaCount: updatedEvent.baristaCount,
              cartCount: updatedEvent.cartCount,
              updatedAt: updatedEvent.updatedAt,
            })
          }),
        })
      }

      // Invalidate drive time — location or date change affects the timeline
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getEventDriveTime,
          transport,
          input: { eventId: updatedEvent.id },
          cardinality: 'finite',
        }),
      })
    },

    onSettled: () => {
      // Refetch lists in background for eventual consistency
      queryClient.invalidateQueries({ queryKey: listEventsKey })

      // Refetch the dashboard aggregate so its event cards stay in sync
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return { mutation }
}

/**
 * Invalidate every cache that can show an event's archived state: the event's
 * own detail caches, all event list caches (both the archived-only and the
 * default variants) and the dashboard aggregate.
 */
function invalidateEventArchiveCaches(
  queryClient: QueryClient,
  transport: Transport,
  eventId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getEvent,
      transport,
      input: { id: eventId },
      cardinality: 'finite',
    }),
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getEventDetail,
      transport,
      input: { id: eventId },
      cardinality: 'finite',
    }),
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEvents,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getDashboardSummary,
      transport,
      cardinality: undefined,
    }),
    refetchType: 'all',
  })
}

/**
 * Hook to archive (soft-delete) an event within a booking
 *
 * This hook:
 * - Archives an event by its ID (soft delete using archived_at)
 * - Automatically invalidates list and detail queries so the archived state shows
 * - Returns success message on completion
 *
 * @param bookingId - The ID of the booking containing the event
 * @returns Mutation object for archiving events
 */
export function useArchiveEvent(_bookingId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(archiveEvent, {
    onSuccess: (_response, variables) => {
      if (!variables.id) return
      invalidateEventArchiveCaches(queryClient, transport, variables.id)
    },
  })

  const archive = async (eventId: string) => {
    return mutation.mutateAsync(
      create(ArchiveEventRequestSchema, { id: eventId })
    )
  }

  return {
    archive,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/** Hook to restore a previously archived event. */
export function useUnarchiveEvent() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(unarchiveEvent, {
    onSuccess: (_response, variables) => {
      if (!variables.id) return
      invalidateEventArchiveCaches(queryClient, transport, variables.id)
    },
  })

  const unarchive = async (eventId: string) => {
    return mutation.mutateAsync(
      create(UnarchiveEventRequestSchema, { id: eventId })
    )
  }

  return {
    unarchive,
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to fetch drive time from org address to the event location
 *
 * @param eventId - The ID of the event to calculate drive time for
 * @returns Drive time in seconds, formatted duration, distance, and query states
 */
/**
 * Hook to resolve an IANA timezone from a free-form postal address via the
 * backend's Google Maps geocoding service. Used by the timezone picker's
 * "Use location timezone" link.
 *
 * The backend returns a Status enum distinguishing OK, NOT_FOUND (the
 * address couldn't be geocoded — user fixes it) and ERROR (the Maps API
 * itself failed — ops alerts). This hook shows an error toast for the
 * ERROR case and collapses both failure modes into a null return so
 * callers render a single "not found, try again" affordance.
 *
 * @returns `resolve(location)` mutator and an `isLoading` flag. `resolve`
 *   returns the IANA zone string on success, or `null` if the location is
 *   empty, unresolvable, or the lookup failed.
 */
export function useResolveLocationTimezone() {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const mutation = useMutation(resolveLocationTimezone)

  const resolve = async (location: string): Promise<string | null> => {
    const trimmed = location.trim()
    if (!trimmed || !selectedOrganizationId) return null
    try {
      const res = await mutation.mutateAsync(
        create(ResolveLocationTimezoneRequestSchema, {
          organizationId: selectedOrganizationId,
          location: trimmed,
        })
      )
      if (res.status === ResolveLocationTimezoneStatus.ERROR) {
        toast.error('Couldn\u2019t reach the timezone service', {
          description: 'Please try again in a moment, or pick a zone manually.',
        })
        return null
      }
      return res.timezone || null
    } catch {
      toast.error('Couldn\u2019t reach the timezone service', {
        description: 'Please try again in a moment, or pick a zone manually.',
      })
      return null
    }
  }

  return { resolve, isLoading: mutation.isPending }
}

export function useGetEventDriveTime(eventId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    getEventDriveTime,
    create(GetEventDriveTimeRequestSchema, { eventId }),
    {
      enabled: isAuthenticated && !!eventId,
    }
  )

  return {
    driveTimeSeconds: query.data?.driveTimeSeconds ?? null,
    distance: query.data?.distance ?? null,
    formattedDuration: query.data?.formattedDuration ?? null,
    isLoading: query.isPending && isAuthenticated && !!eventId,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  }
}
